/**
 * Centralized Auth Session Manager
 *
 * Handles:
 * - Local & session storage keys for JWT token and user profile
 * - 401 session expiration detection and non-looping redirects
 * - Distinction between 401 (session expired) and 403 (forbidden)
 * - Safe preservation of destination URLs and unsaved form data
 * - Concurrency throttling for multiple parallel 401 errors
 */

export const TOKEN_KEY = "lenvia_admin_token";
export const USER_KEY = "lenvia_admin_user";
export const TRUSTED_DEVICE_TOKEN_KEY = "lenvia_admin_trusted_device_token";
export const SESSION_EXPIRED_KEY = "lenvia_session_expired";
export const REDIRECT_FROM_KEY = "lenvia_session_redirect_from";
export const FORM_BACKUP_PREFIX = "lenvia_form_backup_";

const AUTH_EXCLUDED_PATTERNS = [
  /\/admin\/auth\/login(?:\/verify)?$/,
  /\/admin\/auth\/register(?:\/verify)?$/,
  /\/candidate-auth\/request-otp$/,
  /\/candidate-auth\/verify-otp$/,
];

export const isAuthEndpoint = (url = "") => {
  if (!url) return false;
  return AUTH_EXCLUDED_PATTERNS.some((pattern) => pattern.test(url));
};

export const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const getStoredUser = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearStoredSession = ({ clearTrustedDevice = false } = {}) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
    if (clearTrustedDevice) {
      window.localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
    }
  } catch {
    // Ignore storage errors
  }
};

export const setSessionExpiredNotice = (fromPath = null) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
    if (fromPath && typeof fromPath === "string" && !fromPath.includes("/admin/login")) {
      window.sessionStorage.setItem(REDIRECT_FROM_KEY, fromPath);
    }
  } catch {
    // Ignore storage errors
  }
};

export const isSessionExpiredNoticePending = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "true";
  } catch {
    return false;
  }
};

export const consumeSessionExpiredNotice = () => {
  if (typeof window === "undefined") return false;
  try {
    const isExpired = window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "true";
    if (isExpired) {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }
    return isExpired;
  } catch {
    return false;
  }
};

export const getStoredRedirectFrom = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(REDIRECT_FROM_KEY) || null;
  } catch {
    return null;
  }
};

export const consumeStoredRedirectFrom = () => {
  if (typeof window === "undefined") return null;
  try {
    const from = window.sessionStorage.getItem(REDIRECT_FROM_KEY);
    if (from) {
      window.sessionStorage.removeItem(REDIRECT_FROM_KEY);
    }
    return from;
  } catch {
    return null;
  }
};

export const saveFormBackup = (formKey, data) => {
  if (typeof window === "undefined" || !formKey) return;
  try {
    // Form data can contain personal information. Keep emergency backups scoped
    // to the current tab instead of persisting them across browser restarts.
    window.sessionStorage.setItem(`${FORM_BACKUP_PREFIX}${formKey}`, JSON.stringify(data));
  } catch {
    // Ignore storage quota errors
  }
};

export const getFormBackup = (formKey) => {
  if (typeof window === "undefined" || !formKey) return null;
  try {
    const raw = window.sessionStorage.getItem(`${FORM_BACKUP_PREFIX}${formKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearFormBackup = (formKey) => {
  if (typeof window === "undefined" || !formKey) return;
  try {
    window.sessionStorage.removeItem(`${FORM_BACKUP_PREFIX}${formKey}`);
  } catch {
    // Ignore storage errors
  }
};

let isHandlingSessionExpired = false;

export const dispatchSessionExpired = (fromPath = null) => {
  if (typeof window === "undefined") return;

  const currentPath =
    fromPath ||
    (window.location ? `${window.location.pathname}${window.location.search || ""}` : "");

  // Prevent redirect loop if already on login page
  const isAlreadyOnLogin = currentPath.startsWith("/admin/login") || currentPath.startsWith("/candidat/login");

  if (!isAlreadyOnLogin) {
    setSessionExpiredNotice(currentPath);
  }

  clearStoredSession();

  if (isHandlingSessionExpired) {
    return;
  }
  isHandlingSessionExpired = true;

  try {
    window.dispatchEvent(
      new CustomEvent("lenvia:session-expired", {
        detail: {
          from: currentPath,
          timestamp: Date.now(),
        },
      })
    );
  } catch {
    // Ignore dispatch errors
  }

  setTimeout(() => {
    isHandlingSessionExpired = false;
  }, 1200);
};

export const onSessionExpired = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return () => {};
  }
  const handler = (event) => callback(event.detail);
  window.addEventListener("lenvia:session-expired", handler);
  return () => {
    window.removeEventListener("lenvia:session-expired", handler);
  };
};
